<template>
  <div class="container">
    <!-- 操作区域 -->
    <el-card class="operation-card">
      <div class="flex-container">
        <el-input v-model="token" placeholder="令牌值" style="width: 200px"></el-input>
        <el-input type="textarea" v-model="accountIds" :rows="1" placeholder="账户名（每行一个）"
          style="width: 300px; margin: 0 20px"></el-input>
        <el-button type="primary" @click="handleQuery" :loading="queryLoading">查询</el-button>

        <el-divider direction="vertical"></el-divider>

        <el-label>操作金额</el-label>
        <el-input v-model="amount" :min="0" :precision="2" placeholder="操作金额" style="width: 150px"></el-input>

        <!-- 金额类型选择 -->
        <el-select v-model="amountType" placeholder="金额类型" style="width: 100px">
          <el-option label="现金" :value="1"></el-option>
          <el-option label="虚拟金" :value="2"></el-option>
        </el-select>

        <!-- 新增：重试配置弹窗触发器 - 仅在现金模式显示 -->
        <el-button type="danger" @click="openRetryDialog" v-if="amountType === 1">
          现金转出所有（带重试）
        </el-button>
        <!-- 原有的转出所有按钮 - 虚拟金模式使用，现金模式隐藏 -->
        <el-button type="danger" @click="openDialog" v-else>
          转出所有余额
        </el-button>

        <div style="margin-left: 20px">
          <el-button type="success" @click="handleTransfer('IN')">转入</el-button>
          <el-button type="danger" @click="handleTransfer('OUT')">转出</el-button>
        </div>
      </div>
    </el-card>

    <!-- 重试配置弹窗 - 仅用于现金转出 -->
    <el-dialog title="现金转出重试配置" :visible.sync="retryDialogVisible" width="30%" @close="resetRetryForm">
      <el-form :model="retryForm" :rules="retryRules" ref="retryForm">
        <el-form-item label="调用失败后金额缩减区间" prop="step">
          <el-input v-model.number="retryForm.step" type="number" placeholder="请输入大于0的金额区间"></el-input>
          <div style="color: #909399; font-size: 12px; margin-top: 5px">
            当提示余额不足时，将按此金额递减重试，最多重试20次
          </div>
        </el-form-item>
      </el-form>

      <span slot="footer" class="dialog-footer">
        <el-button @click="retryDialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="confirmRetryConfig">确 定</el-button>
      </span>
    </el-dialog>

    <!-- 数据表格 -->
    <el-card class="data-card">
      <el-table :data="tableData" border @selection-change="handleSelectionChange" style="width: 100%"
        @sort-change="handleSortChange">
        <el-table-column type="selection" width="55"></el-table-column>
        <el-table-column prop="companyName" label="广告主公司"></el-table-column>
        <el-table-column prop="name" label="广告主用户"></el-table-column>
        <el-table-column prop="cashBalance" label="现金余额" sortable
          :sort-method="(a, b) => parseFloat(a.cashBalance) - parseFloat(b.cashBalance)"></el-table-column>
        <!-- 虚拟金余额列 -->
        <el-table-column label="总金额(现金+虚拟金)" align="center" min-width="150" sortable :sort-method="(a, b) => {
          const totalA = parseFloat(a.cashBalance || 0) + parseFloat(a.discountBalance || 0);
          const totalB = parseFloat(b.cashBalance || 0) + parseFloat(b.discountBalance || 0);
          return totalA - totalB;
        }">
          <template #default="scope">
            <span style="color: #409EFF; font-weight: bold">
              {{ (parseFloat(scope.row.cashBalance || 0) + parseFloat(scope.row.discountBalance || 0)).toFixed(2) }} 元
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="todayConsume" label="今日消耗" sortable
          :sort-method="(a, b) => parseFloat(a.todayConsume) - parseFloat(b.todayConsume)"></el-table-column>
        <el-table-column prop="yestConsume" label="昨日消耗" sortable
          :sort-method="(a, b) => parseFloat(a.yestConsume) - parseFloat(b.yestConsume)"></el-table-column>
        <el-table-column label="转账状态" width="100" align="center">
          <template #default="{ row }">
            <el-tag :type="getTransferStatusType(row.transferStatus)" size="small" effect="dark"
              v-if="row.transferStatus === 1">
              转账中
            </el-tag>
            <span v-else>-</span>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 转出所有确认弹窗（用于普通转出，虚拟金使用） -->
    <el-dialog title="转出所有确认" :visible.sync="transferAllDialogVisible" width="60%"
      @close="transferAllDialogVisible = false">
      <div class="transfer-confirm-content">
        <div class="transfer-type-info">
          <el-tag :type="amountType === 1 ? 'success' : 'warning'" size="medium">
            当前操作类型：{{ transferAmountTypeText }}转出
          </el-tag>
          <el-alert title="以下账户将执行转出所有操作" type="info" :closable="false" show-icon style="margin-top: 10px">
          </el-alert>
        </div>

        <!-- 账户列表表格 -->
        <el-table :data="transferAccountsList" border style="width: 100%; margin-top: 15px" max-height="400">
          <el-table-column prop="companyName" label="广告主公司" min-width="150"></el-table-column>
          <el-table-column prop="userName" label="广告主用户" min-width="150"></el-table-column>
          <el-table-column prop="balance" label="转出金额" min-width="120">
            <template slot-scope="scope">
              <span style="color: #F56C6C; font-weight: bold">{{ scope.row.balance }} 元</span>
            </template>
          </el-table-column>
          <el-table-column label="金额类型" min-width="100">
            <template slot-scope="scope">
              <el-tag size="small" :type="amountType === 1 ? 'success' : 'warning'">
                {{ amountType === 1 ? '现金' : '虚拟金' }}
              </el-tag>
            </template>
          </el-table-column>
        </el-table>

        <!-- 统计信息 -->
        <div class="transfer-statistics" style="margin-top: 15px; text-align: right">
          <span>共 <strong>{{ transferAccountsList.length }}</strong> 个账户，</span>
          <span>总转出金额：<strong style="color: #F56C6C; font-size: 16px">
              {{ totalTransferAmount }} 元
            </strong></span>
        </div>

        <!-- 警告信息 -->
        <el-alert title="此操作将转出所选账户的全部余额，请谨慎确认" type="warning" :closable="false" show-icon style="margin-top: 15px">
        </el-alert>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button @click="transferAllDialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="confirmTransferAll" :loading="transferAllLoading">
          确 认 转 出
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import axios from "axios";

export default {
  data() {
    // 自定义验证规则
    const validateNumber = (rule, value, callback) => {
      if (value === "" || value === null) {
        callback(new Error("请输入数字"));
      } else if (value <= 0) {
        callback(new Error("数字必须大于0"));
      } else {
        callback();
      }
    };

    return {
      token: '',
      accountIds: '',
      amount: 0,
      amountType: 1, // 金额类型，1-现金，2-虚拟金
      tableData: [],
      sortProp: '',
      sortOrder: '',
      selectedData: [], // 选中的数据
      queryLoading: false, // 查询加载状态

      // 重试相关
      retryDialogVisible: false, // 重试配置弹窗
      retryForm: {
        step: null // 缩减步长
      },
      retryRules: {
        step: [
          { required: true, validator: validateNumber, trigger: "blur" }
        ]
      },
      cashTransferLoading: false, // 现金转出加载状态

      // 原有的转出所有相关
      transferAllDialogVisible: false, // 转出所有确认弹窗
      transferAccountsList: [], // 要显示的账户列表
      transferAmountTypeText: '', // 金额类型文本
      transferAllLoading: false // 转出所有加载状态
    }
  },
  computed: {
    // 计算总转出金额
    totalTransferAmount() {
      return this.transferAccountsList
        .reduce((sum, item) => sum + parseFloat(item.balance), 0)
        .toFixed(2);
    }
  },
  methods: {
    // ==================== 现金转出（带重试机制）====================
    // 打开重试配置弹窗
    openRetryDialog() {
      if (this.selectedData.length === 0) {
        this.$message.warning('请先选择要操作的账户');
        return;
      }
      this.retryDialogVisible = true;
    },

    // 重置重试表单
    resetRetryForm() {
      this.$refs.retryForm.resetFields();
    },

    // 确认重试配置，执行现金转出
    confirmRetryConfig() {
      this.$refs.retryForm.validate(valid => {
        if (valid) {
          // 确保step是有效数字
          const step = parseFloat(this.retryForm.step);
          if (isNaN(step) || step <= 0) {
            this.$message.error('请输入有效的大于0的数字');
            return;
          }

          console.log("缩减步长：", step);
          this.retryDialogVisible = false;
          this.executeCashTransferWithRetry();
        }
      });
    },

    // 执行带重试的现金转出
    async executeCashTransferWithRetry() {
      // 先检查缩减步长是否有效
      const step = parseFloat(this.retryForm.step);
      if (isNaN(step) || step <= 0) {
        this.$message.error('请先设置有效的缩减步长（大于0的数字）');
        this.cashTransferLoading = false;
        return;
      }

      console.log('使用缩减步长:', step);

      const accounts = this.selectedData.filter(user => {
        return parseFloat(user.cashBalance) > 0;
      });

      if (accounts.length === 0) {
        this.$message.warning('所选账户的现金余额均为0，无法转出');
        return;
      }

      this.cashTransferLoading = true;

      // 显示加载提示
      const loading = this.$loading({
        lock: true,
        text: '正在处理现金转出请求（带重试机制）...',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      });

      const requests = accounts.map(user => {
        return (async () => {
          let currentAmount = parseFloat(user.cashBalance);
          const MAX_RETRIES = 20;
          let attempts = 0;

          console.log(`开始处理账户 ${user.companyName || user.name}，初始金额: ${currentAmount} 元`);

          while (attempts++ < MAX_RETRIES) {
            try {
              console.log(`第${attempts}次尝试，金额: ${currentAmount} 元`);

              // 发送请求并获取响应
              const res = await this.sendTransferRequest({
                accountId: user.uuid,
                amount: currentAmount,
                type: "OUT",
                token: this.token,
                amountType: 1 // 现金
              });

              console.log(`账户 ${user.companyName || user.name} 转出成功，金额: ${currentAmount} 元`);

              // 成功时直接返回
              return {
                success: true,
                user: user.name || user.uuid,
                amount: currentAmount,
                companyName: user.companyName
              };
            } catch (error) {
              console.log(`第${attempts}次尝试失败，当前金额: ${currentAmount}`, error);

              // 判断是否是余额不足的错误
              let isInsufficientBalance = false;

              // 通过错误标记判断
              if (error.isInsufficientBalance === true) {
                isInsufficientBalance = true;
              }

              // 通过错误消息判断
              if (!isInsufficientBalance) {
                let errorMsg = '';
                if (error.message) {
                  errorMsg = error.message;
                }
                if (error.response && error.response.data) {
                  const data = error.response.data;
                  if (data.message) {
                    errorMsg = data.message;
                  } else if (data.msg) {
                    errorMsg = data.msg;
                  }
                }

                console.log('错误消息:', errorMsg);

                const insufficientKeywords = [
                  "转账金额不足",
                  "转出金额大于当前账户余额",
                  "余额不足",
                  "INSUFFICIENT_BALANCE",
                  "金额不足"
                ];

                isInsufficientBalance = insufficientKeywords.some(keyword =>
                  errorMsg.includes(keyword)
                );
              }

              if (isInsufficientBalance) {
                // 减少金额 - 使用之前保存的step变量
                const oldAmount = currentAmount;
                currentAmount = parseFloat((currentAmount - step).toFixed(2));

                console.log(`余额不足，从 ${oldAmount} 元减少 ${step} 元，新金额: ${currentAmount} 元`);

                // 金额耗尽处理
                if (currentAmount <= 0) {
                  console.log(`账户 ${user.companyName || user.name} 金额耗尽，停止重试`);
                  return {
                    success: false,
                    user: user.name || user.uuid,
                    amount: oldAmount,
                    companyName: user.companyName,
                    error: '可用金额不足'
                  };
                }

                // 添加延迟防止高频请求
                await new Promise(resolve => setTimeout(resolve, 100));
                continue; // 继续重试
              }

              // 其他错误，返回失败结果
              console.log(`账户 ${user.companyName || user.name} 发生其他错误:`, error);
              return {
                success: false,
                user: user.name || user.uuid,
                amount: currentAmount,
                companyName: user.companyName,
                error: error.message || '未知错误'
              };
            }
          }

          // 超过最大重试次数
          return {
            success: false,
            user: user.name || user.uuid,
            amount: currentAmount,
            companyName: user.companyName,
            error: '超过最大重试次数'
          };
        })();
      });

      try {
        const results = await Promise.all(requests);

        const success = results.filter(r => r && r.success).length;
        const failed = results.filter(r => r && !r.success).length;

        loading.close();
        this.cashTransferLoading = false;

        if (failed === 0) {
          this.$message.success(`所有账户现金转出成功，共处理${success}个账户`);
        } else if (success === 0) {
          this.$message.error(`全部失败`);
        } else {
          this.$message.warning(`部分成功：${success}个成功，${failed}个失败`);
          results.forEach(result => {
            if (result && !result.success) {
              console.error(`账户 ${result.user} 失败：`, result.error);
            }
          });
        }

        this.handleQuery();
      } catch (error) {
        loading.close();
        this.cashTransferLoading = false;
        this.$message.error('操作失败：' + error.message);
      }
    },

    // ==================== 原有的转出所有（用于虚拟金）====================
    // 打开转出所有确认弹窗（用于虚拟金）
    openDialog() {
      if (this.selectedData.length === 0) {
        this.$message.warning('请先选择要操作的账户');
        return;
      }

      // 设置金额类型文本
      this.transferAmountTypeText = this.amountType === 1 ? '现金' : '虚拟金';

      // 构建账户列表数据
      this.transferAccountsList = this.selectedData.map(user => {
        const balance = this.amountType === 1
          ? parseFloat(user.cashBalance).toFixed(2)
          : parseFloat(user.discountBalance).toFixed(2);

        return {
          id: user.uuid,
          companyName: user.companyName || '-',
          userName: user.name || '-',
          balance: balance,
          originalBalance: this.amountType === 1 ? user.cashBalance : user.discountBalance
        };
      }).filter(item => parseFloat(item.originalBalance) > 0); // 过滤掉余额为0的账户

      // 如果所有账户余额都为0
      if (this.transferAccountsList.length === 0) {
        this.$message.warning('所选账户的' + this.transferAmountTypeText + '余额均为0，无法转出');
        return;
      }

      // 显示自定义弹窗
      this.transferAllDialogVisible = true;
    },

    // 确认转出所有（用于虚拟金，无重试机制）
    async confirmTransferAll() {
      // 关闭弹窗
      this.transferAllDialogVisible = false;
      this.transferAllLoading = true;

      // 使用过滤后的账户列表（已过滤掉余额为0的）
      const accounts = this.selectedData.filter(user => {
        const balance = this.amountType === 1
          ? parseFloat(user.cashBalance)
          : parseFloat(user.discountBalance);
        return balance > 0;
      });

      const amountTypeText = this.amountType === 1 ? '现金' : '虚拟金';

      const requests = accounts.map(user => {
        return (async () => {
          // 根据金额类型获取对应余额（直接取全部）
          const totalAmount = this.amountType === 1
            ? parseFloat(user.cashBalance)
            : parseFloat(user.discountBalance);

          try {
            // 发送转出请求（转出全部金额）
            const res = await this.sendTransferRequest({
              accountId: user.uuid,
              amount: totalAmount,
              type: "OUT",
              token: this.token,
              amountType: this.amountType
            });

            console.log(`账户 ${user.companyName || user.name} 转出${amountTypeText}成功，金额：${totalAmount}`);
            return { success: true, user: user.name, amount: totalAmount };
          } catch (error) {
            console.error(`账户 ${user.companyName || user.name} 转出失败:`, error);
            return { success: false, user: user.name, error: error.message };
          }
        })();
      });

      // 显示加载提示
      const loading = this.$loading({
        lock: true,
        text: '正在处理转出请求...',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      });

      try {
        const results = await Promise.all(requests);

        const success = results.filter(r => r.success).length;
        const failed = results.filter(r => !r.success).length;

        loading.close();
        this.transferAllLoading = false;

        if (failed === 0) {
          this.$message.success(`所有账户${amountTypeText}转出成功，共处理${success}个账户`);
        } else if (success === 0) {
          this.$message.error(`全部失败：${results[0].error || '未知错误'}`);
        } else {
          this.$message.warning(`部分成功：${success}个成功，${failed}个失败`);
          // 输出失败详情
          results.forEach(result => {
            if (!result.success) {
              console.error(`账户 ${result.user} 失败：`, result.error);
            }
          });
        }

        this.handleQuery();
      } catch (error) {
        loading.close();
        this.transferAllLoading = false;
        this.$message.error('操作失败：' + error.message);
      }
    },

    // ==================== 公共方法 ====================
    handleSelectionChange(selection) {
      this.selectedData = selection;
      console.log('选中的数据:', selection);
    },

    async handleQuery() {
      if (!this.validateQueryParams()) return;

      this.queryLoading = true;
      this.tableData = [];

      const accountIds = this.getAccountIds();
      try {
        for (const accountId of accountIds) {
          const data = await this.fetchAccountData(this.token, accountId);
          console.log(data);

          const matchedItem = data.data.data.list.find(obj => obj.name === accountId);

          this.tableData.push(
            matchedItem
              ? matchedItem
              : data.data.data.list[0]
          );
        }
      } catch (error) {
        this.$message.error('查询失败：' + error.message);
      } finally {
        this.queryLoading = false;
      }
    },

    async fetchAccountData(cookie, accountId) {
      return await axios.post('https://control.zjwec.cn/api/vivo/subAccountList', {
        token: cookie,
        accountId: accountId
      })
    },

    validateQueryParams() {
      if (!this.token) {
        this.$message.error('请输入令牌值');
        return false;
      }
      if (this.getAccountIds().length === 0) {
        this.$message.error('请输入至少一个账户ID');
        return false;
      }
      return true;
    },

    getAccountIds() {
      return this.accountIds.split('\n')
        .map(id => id.trim())
        .filter(id => id);
    },

    async handleTransfer(type) {
      if (this.selectedData.length === 0) {
        this.$message.warning('请先选择要操作的账户');
        return;
      }

      if (!this.amount || this.amount <= 0) {
        this.$message.warning('请输入操作金额');
        return;
      }

      const accounts = this.selectedData;

      // 显示加载提示
      const loading = this.$loading({
        lock: true,
        text: '正在处理请求...',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      });

      const requests = accounts.map(user =>
        this.sendTransferRequest({
          accountId: user.uuid,
          amount: this.amount,
          type: type,
          token: this.token,
          amountType: this.amountType
        })
      );

      try {
        await Promise.all(requests);
        loading.close();
        this.$message.success('操作成功');
        this.handleQuery();
      } catch (error) {
        loading.close();
        console.error('操作失败:', error);
        this.$message.error('部分操作失败');
      }
    },

    getTransferStatusType(status) {
      if (status === 1) {
        return 'warning'; // 转账中显示黄色警告色
      }
      return 'info';
    },

    handleRowTransfer(row, type) {
      this.amount = type === 'OUT'
        ? (this.amountType === 1 ? row.cashBalance : row.discountBalance)
        : 0;
      this.accountIds = row.accountId;
      this.handleTransfer(type);
    },

    handleSortChange({ prop, order }) {
      this.sortProp = prop;
      this.sortOrder = order;
    },

    async sendTransferRequest(params) {
      let url = '';

      if (params.type === "IN") {
        url = 'https://control.zjwec.cn/api/vivo/secondTransferIn';
      } else {
        url = 'https://control.zjwec.cn/api/vivo/secondTransferOut';
      }

      try {
        let response;

        // 现金转账
        if (params.amountType == 1) {
          response = await this.$axios.post(url, {
            token: params.token,
            accountId: params.accountId,
            amount: params.amount.toString()
          });
        }
        // 虚拟金转账
        else {
          response = await this.$axios.post(url, {
            token: params.token,
            accountId: params.accountId,
            amount: params.amount.toString(),
            amountType: 2
          });
        }

        console.log('转账响应:', response.data);

        // 检查是否是余额不足的错误 (retcode=1, message包含"转账金额不足")
        if (response.data && response.data.retcode == 1) {
          // 创建一个包含完整错误信息的错误对象
          const error = new Error(response.data.message || '转账金额不足');
          error.response = response;
          error.isInsufficientBalance = true; // 添加标记，便于识别
          error.retcode = response.data.retcode;
          throw error;
        }

        // 检查是否成功 (retcode=0 表示成功)
        if (response.data && response.data.retcode == 0) {
          return response;
        } else {
          const error = new Error(response.data.message || '转账失败');
          error.response = response;
          error.isInsufficientBalance = false;
          throw error;
        }

      } catch (error) {
        console.error('转账请求失败:', error);

        // 如果是axios网络错误，包装一下
        if (!error.response) {
          const networkError = new Error(error.message || '网络请求失败');
          networkError.isInsufficientBalance = false;
          throw networkError;
        }

        throw error; // 确保错误被抛出到上层
      }
    }
  }
}
</script>

<style scoped>
/* 容器样式 */
.container {
  padding: 20px;
}

/* 操作区域卡片样式 */
.operation-card {
  margin-bottom: 20px;
}

/* 操作区域布局样式 */
.flex-container {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

/* 数据表格卡片样式 */
.data-card .el-table {
  margin-top: 20px;
}

/* 表格内链接样式 */
.data-card .el-table .el-link {
  margin: 0 5px;
}

/* 转出确认弹窗样式 */
.transfer-confirm-content {
  padding: 10px 0;
}

.transfer-type-info {
  margin-bottom: 15px;
}

.transfer-statistics {
  padding: 10px;
  background-color: #f5f7fa;
  border-radius: 4px;
}

/* 弹窗中的表格样式优化 */
.transfer-confirm-content .el-table th {
  background-color: #f5f7fa;
  color: #606266;
}

.transfer-confirm-content .el-table td {
  padding: 8px 0;
}

/* 移动端适配 */
@media (max-width: 768px) {
  .flex-container {
    flex-direction: column;
    align-items: flex-start !important;
  }

  .flex-container>* {
    margin: 5px 0 !important;
    width: 100% !important;
  }

  .flex-container .el-divider--vertical {
    display: none;
  }

  .el-dialog {
    width: 90% !important;
  }

  .transfer-statistics {
    text-align: left !important;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/login/zhzqian.vue